import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LucideAngularModule, X, Upload } from 'lucide-angular';
import { ModalService } from '../../../../core/services/modal.service';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { UserService } from '../../../../core/services/user.service';
import { SwalService } from '../../../../core/services/swal.service';
import {
  DocumentService,
  CreateDocumentDto,
} from '../../../../core/services/document.service';

@Component({
  selector: 'app-add-document-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LucideAngularModule,
  ],
  templateUrl: './add-document-modal.component.html',
  styleUrls: ['./add-document-modal.component.scss'],
})
export class AddDocumentModalComponent implements OnInit {
  readonly X = X;
  readonly Upload = Upload;

  form: FormGroup;
  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;
  selectedFile: File | null = null;
  documentTypes: any[] = [];
  subAccounts: any[] = [];
  currentUser: any = null;
  selectedPatientId: string = '';

  // Types par défaut en cas d'erreur
  defaultDocumentTypes = [
    { value: 'prescription', label: 'Prescription médicale' },
    { value: 'ordonnance', label: 'Ordonnance' },
    { value: 'certificat', label: 'Certificat médical' },
    { value: 'analyse', label: "Résultats d'analyse" },
    { value: 'radio', label: 'Radiographie' },
    { value: 'scanner', label: 'Scanner' },
    { value: 'irm', label: 'IRM' },
    { value: 'echographie', label: 'Échographie' },
    { value: 'autre', label: 'Autre document' },
  ];

  constructor(
    private fb: FormBuilder,
    private modalService: ModalService,
    private documentService: DocumentService,
    private userService: UserService,
    private swalService: SwalService
  ) {
    this.form = this.fb.group({
      patientId: ['', Validators.required],
      documentType: ['', Validators.required],
      professional: ['', [this.noWhitespaceOnlyValidator()]],
      file: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    console.log(
      "AddDocumentModalComponent: Modal d'ajout de document initialisé"
    );

    // Récupérer les données passées au modal
    const modalData = ModalDataService.getData();
    console.log('AddDocumentModalComponent: Données du modal:', modalData);

    if (modalData) {
      this.documentTypes = modalData.documentTypes || this.defaultDocumentTypes;
      this.subAccounts = modalData.subAccounts || [];
      this.currentUser = modalData.currentUser || null;
    } else {
      this.documentTypes = this.defaultDocumentTypes;
    }

    // Charger les sous-comptes si ils ne sont pas disponibles
    this.loadSubAccountsIfNeeded();

    // Surveiller les changements du champ documentType
    this.form.get('documentType')?.valueChanges.subscribe((value) => {
      console.log('documentType changé:', value);
    });

    console.log(
      'AddDocumentModalComponent: Types de documents disponibles:',
      this.documentTypes
    );
    console.log(
      'AddDocumentModalComponent: Valeurs des types de documents:',
      this.documentTypes.map((t) => ({ value: t.value, label: t.label }))
    );
    console.log(
      'AddDocumentModalComponent: Sous-comptes disponibles:',
      this.subAccounts
    );
    console.log(
      'AddDocumentModalComponent: Utilisateur courant:',
      this.currentUser
    );
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.form.patchValue({ file: file });
      console.log('Fichier sélectionné:', file.name, 'Taille:', file.size);
    }
  }

  onProfessionalChange(event: any): void {
    const value = event.target.value;
    // Nettoyer automatiquement les espaces en début et fin
    if (value && value !== value.trim()) {
      this.form.patchValue({ professional: value.trim() });
    }
  }

  selectPatient(patientId: string): void {
    this.selectedPatientId = patientId;
    this.form.patchValue({ patientId: patientId });
    console.log('Patient sélectionné:', patientId);
  }

  getPatientName(patient: any): string {
    if (patient.firstName && patient.lastName) {
      return `${patient.firstName} ${patient.lastName}`;
    }
    return patient.passportNumber || 'Patient inconnu';
  }

  private loadSubAccountsIfNeeded(): void {
    // Si les sous-comptes ne sont pas disponibles, les récupérer depuis l'API
    if (!this.subAccounts || this.subAccounts.length === 0) {
      console.log(
        "AddDocumentModalComponent: Aucun sous-compte disponible, récupération depuis l'API..."
      );

      this.userService.getSubAccounts().subscribe({
        next: (response: any) => {
          console.log(
            'AddDocumentModalComponent: Réponse API sous-comptes:',
            response
          );
          const subAccounts =
            response.subAccounts || response.data || response || [];
          console.log(
            'AddDocumentModalComponent: Sous-comptes récupérés:',
            subAccounts
          );

          this.subAccounts = subAccounts;
        },
        error: (error: any) => {
          console.error(
            'AddDocumentModalComponent: Erreur lors de la récupération des sous-comptes:',
            error
          );
          this.swalService.error(
            'Erreur',
            'Impossible de charger les informations des patients'
          );
        },
      });
    } else {
      console.log(
        'AddDocumentModalComponent: Sous-comptes déjà disponibles:',
        this.subAccounts
      );
    }
  }

  onSubmit(): void {
    // Marquer tous les champs comme touchés pour afficher les erreurs
    this.markFormGroupTouched();

    // Validation personnalisée
    if (!this.selectedFile) {
      this.error = 'Veuillez sélectionner un fichier';
      return;
    }

    if (!this.selectedPatientId) {
      this.error = 'Veuillez sélectionner un patient';
      return;
    }

    if (!this.form.value.documentType) {
      this.error = 'Veuillez sélectionner un type de document';
      return;
    }

    // Validation du champ professionnel (optionnel mais avec règles si rempli)
    const professionalValue = this.form.value.professional;
    if (professionalValue && professionalValue.trim().length < 2) {
      this.error =
        'Le nom du professionnel doit contenir au moins 2 caractères';
      return;
    }

    if (this.form.valid && this.selectedFile && this.selectedPatientId) {
      this.loading = true;
      this.error = null;

      const documentData: CreateDocumentDto = {
        documentType: this.form.value.documentType,
        professionalName: professionalValue
          ? professionalValue.trim()
          : undefined,
        file: this.selectedFile,
      };

      this.documentService
        .uploadDocument(this.selectedPatientId, documentData)
        .subscribe({
          next: (response: any) => {
            console.log(
              'AddDocumentModalComponent: Document ajouté avec succès:',
              response
            );
            this.loading = false;
            this.success = true;

            // Émettre un événement pour informer le composant parent
            this.modalService.emitEvent({
              type: 'documentAdded',
              data: {
                patientId: this.selectedPatientId,
                documentType: this.form.value.documentType,
                professional: documentData.professionalName,
                fileName: this.selectedFile?.name,
                fileSize: this.selectedFile?.size,
                response: response,
              },
            });

            // Le modal sera fermé par le composant parent
          },
          error: (error: any) => {
            console.error(
              "AddDocumentModalComponent: Erreur lors de l'ajout du document:",
              error
            );
            this.loading = false;
            this.error =
              error.error?.message || "Erreur lors de l'ajout du document";
          },
        });
    }
  }

  markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  closeModal(): void {
    this.modalService.closeAll();
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Ce champ est requis';
      }
      if (control.errors['minlength']) {
        return `Minimum ${control.errors['minlength'].requiredLength} caractères`;
      }
      if (control.errors['whitespaceOnly']) {
        return 'Ce champ ne peut pas contenir uniquement des espaces';
      }
      if (control.errors['minLength']) {
        return `Minimum ${control.errors['minLength'].requiredLength} caractères (actuellement ${control.errors['minLength'].actualLength})`;
      }
    }
    return '';
  }

  getDocumentTypeLabel(value: string): string {
    const type = this.documentTypes.find((t) => t.value === value);
    return type ? type.label : value;
  }

  /**
   * Validateur personnalisé pour empêcher les chaînes contenant uniquement des espaces
   */
  private noWhitespaceOnlyValidator() {
    return (control: any) => {
      if (!control.value) {
        return null; // Champ optionnel, pas d'erreur si vide
      }

      const trimmedValue = control.value.toString().trim();
      if (trimmedValue.length === 0) {
        return { whitespaceOnly: true };
      }

      if (trimmedValue.length < 2) {
        return {
          minLength: { requiredLength: 2, actualLength: trimmedValue.length },
        };
      }

      return null;
    };
  }

  /**
   * Vérifie si le formulaire est valide pour la soumission
   */
  isFormValid(): boolean {
    return (
      this.selectedFile !== null &&
      this.selectedPatientId !== '' &&
      this.form.value.documentType !== '' &&
      this.form.get('professional')?.valid !== false
    );
  }

  /**
   * Calcule le pourcentage de progression du formulaire
   */
  getFormProgress(): number {
    let progress = 0;
    const totalFields = 4;

    if (this.selectedFile) progress += 25;
    if (this.selectedPatientId) progress += 25;
    if (this.form.value.documentType) progress += 25;
    if (this.form.value.professional && this.form.get('professional')?.valid) {
      progress += 25;
    } else if (!this.form.value.professional) {
      progress += 25; // Champ optionnel vide = valide
    }

    return progress;
  }
}
